<template>
    <div
        class="absolute left-full top-0 ml-3 w-auto rounded-md shadow-sm bg-white text-gray-700 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-offset-gray-100 focus:ring-blue-50 border border-gray-300">
        <div v-for="subCategory in categoriesData" :key="subCategory.id" class="relative">
            <!-- Button for subcategories with further subcategories -->
            <button @click.stop="selectCategoryOrToggle(subCategory)" type="button"
                class="flex justify-between items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
                {{ subCategory.name }}
                <font-awesome-icon v-if="subCategory.categories && subCategory.categories.length > 0"
                    :icon="['fas', 'chevron-right']"
                    :class="{ 'rotate-90': activeSubcategory && activeSubcategory.id === subCategory.id }"
                    class="h-3 w-3 ml-1 transition-transform duration-200" />
            </button>

            <!-- Recursively load more subcategories if they exist -->
            <SubcategoryDropdown v-if="activeSubcategory && activeSubcategory.id === subCategory.id"
                :categories="subCategory.categories" @categorySelected="selectSubCategory"
                @resetDropdownStates="resetDropdownStates" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'; // Import FontAwesomeIcon for icons
import SubcategoryDropdown from './SubcategoryDropdown.vue'; // Import itself for recursive usage

// Define the props and emits for the component
const props = defineProps(['categories']);
const emit = defineEmits(['categorySelected', 'resetDropdownStates']); // Correctly define emits

const categoriesData = computed(() => props.categories); // Use computed to maintain reactivity
const activeSubcategory = ref(null); // Track the currently active subcategory

// Watch for changes in activeSubcategory to handle state resets
watch(activeSubcategory, (newVal) => {
    console.log('SubcategoryDropdown - activeSubcategory changed:', newVal);
    if (!newVal) {
        emit('resetDropdownStates');
    }
});

// Select category or toggle subcategory dropdown
const selectCategoryOrToggle = (subCategory) => {
    const hasSubCategories = subCategory.categories && subCategory.categories.length > 0;

    if (hasSubCategories) {
        // Toggle visibility of the subcategory dropdown
        if (activeSubcategory.value && activeSubcategory.value.id === subCategory.id) {
            activeSubcategory.value = null; // Close if the same subcategory is clicked
        } else {
            activeSubcategory.value = subCategory; // Open the selected subcategory
        }
    } else {
        activeSubcategory.value = null; // Close all dropdowns if no further subcategories exist
        emit('categorySelected', subCategory);
    }

    // Only emit resetDropdownStates if there's no active subcategory left
    if (!activeSubcategory.value) {
        emit('resetDropdownStates');
    }
};

// Handle subcategory selection
const selectSubCategory = (subCategory) => {
    const hasSubCategories = subCategory.categories && subCategory.categories.length > 0;

    if (hasSubCategories) {
        activeSubcategory.value = subCategory; // Open the nested subcategory dropdown
    } else {
        activeSubcategory.value = null; // Close all dropdowns
        emit('categorySelected', subCategory);
    }

    // Emit resetDropdownStates only if necessary
    if (!activeSubcategory.value) {
        emit('resetDropdownStates');
    }
};

// Reset state when receiving reset event
const resetDropdownStates = () => {
    activeSubcategory.value = null;
};

</script>

<style scoped>
.rotate-90 {
    transform: rotate(90deg);
    /* Rotate 90 degrees when active */
}

.transition-transform {
    transition: transform 200ms;
    /* Smooth transition for the rotation */
}
</style>
